// app/hooks/use-token-refresh.ts  (Phase 6)
//
// Keeps a logged-in budflow session alive by rotating the ~1h access_token
// before it expires (via /api/auth/refresh). Without this, an active session
// silently degrades to guest after an hour — losing the personalised menu,
// member pricing, and cart ownership — while the UI still shows "logged in"
// (the catalystUserId cookie lasts a year). Mount once, high in the tree.
//
// Race handling:
//  - Multi-tab: GoTrue rotates the refresh token on each use, so two tabs
//    refreshing at once → one gets 401. If the shared token_expires_at is still
//    in the future, another tab already refreshed — reschedule off it. Only stop
//    when the token is actually dead.
//  - Logout: listen for `user:logout` to stop; if a refresh lands after logout,
//    re-clear the tokens it re-set so logout isn't silently undone.
import { useEffect, useRef } from 'react';
import { clearLoginState } from '~/utils/auth-cleanup';

const REFRESH_BUFFER_MS = 5 * 60 * 1000; // refresh 5 min before expiry
const MIN_DELAY_MS = 10 * 1000; // never busy-loop if already past due
const RETRY_DELAY_MS = 60 * 1000; // transient failure → retry in a minute

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return m ? m[2] : null;
}

function expiryMs(): number {
  const raw = readCookie('token_expires_at');
  const ms = raw ? parseInt(raw, 10) * 1000 : NaN;
  return Number.isFinite(ms) ? ms : NaN;
}

export function useTokenRefresh() {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    let loggedOut = false;
    let generation = 0;

    const clear = () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };

    async function refresh() {
      const started = generation;
      try {
        const res = await fetch('/api/auth/refresh', { method: 'POST' });

        if (cancelled || started !== generation) {
          // A completed refresh can set cookies even after explicit logout.
          if (loggedOut) clearLoginState();
          return;
        }

        if (res.ok) {
          const data = await res.json().catch(() => null);
          if (cancelled || started !== generation) {
            if (loggedOut) clearLoginState();
            return;
          }
          if (data?.access_token) {
            localStorage.setItem('access_token', data.access_token);
            if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
            if (data.expires_at) localStorage.setItem('token_expires_at', String(data.expires_at));
            window.dispatchEvent(new Event('auth:refreshed'));
          }
          await validate(false);
          return;
        }

        if (res.status === 401) {
          const exp = expiryMs();
          if (Number.isFinite(exp) && exp - Date.now() > REFRESH_BUFFER_MS) {
            await validate(false);
          } else expire();
          return;
        }

        clear();
        timer.current = setTimeout(refresh, RETRY_DELAY_MS);
      } catch {
        if (cancelled) return;
        clear();
        timer.current = setTimeout(refresh, RETRY_DELAY_MS);
      }
    }

    function expire() {
      clearLoginState();
      clear();
      window.dispatchEvent(new Event('user:logout'));
      window.dispatchEvent(new Event('auth:expired'));
    }

    async function validate(allowRefresh = true) {
      const started = generation;
      const snapshot = readCookie('access_token');
      try {
        const response = await fetch('/api/auth/session', { cache: 'no-store' });
        if (cancelled || started !== generation) return;
        if (snapshot !== readCookie('access_token')) {
          await validate();
          return;
        }
        if (response.ok) {
          const data = await response.json();
          if (cancelled || started !== generation) return;
          document.cookie = `catalystUserId=${encodeURIComponent(data.userId)}; path=/; max-age=31536000; SameSite=Lax`;
          localStorage.setItem('catalystUserId', data.userId);
          window.dispatchEvent(new Event('auth:validated'));
          schedule();
        } else if (response.status === 401) {
          if (allowRefresh && readCookie('refresh_token')) await refresh();
          else expire();
        } else {
          clear();
          timer.current = setTimeout(validate, RETRY_DELAY_MS);
        }
      } catch {
        if (!cancelled) {
          clear();
          timer.current = setTimeout(validate, RETRY_DELAY_MS);
        }
      }
    }

    function schedule() {
      clear();
      if (cancelled) return;
      if (!readCookie('refresh_token')) return; // logged-out — nothing to refresh
      const exp = expiryMs();
      if (!Number.isFinite(exp)) return;
      const delay = Math.max(MIN_DELAY_MS, exp - Date.now() - REFRESH_BUFFER_MS);
      timer.current = setTimeout(refresh, delay);
    }

    const onLogout = () => {
      loggedOut = true;
      generation++;
      cancelled = true;
      clearLoginState();
      clear();
    };
    const onLogin = () => {
      generation++;
      cancelled = false;
      loggedOut = false;
      void validate();
    };
    window.addEventListener('user:logout', onLogout);
    window.addEventListener('user:login', onLogin);

    if (
      readCookie('catalystUserId') ||
      readCookie('access_token') ||
      readCookie('refresh_token') ||
      localStorage.getItem('catalystUserId')
    )
      void validate();
    return () => {
      cancelled = true;
      clear();
      window.removeEventListener('user:logout', onLogout);
      window.removeEventListener('user:login', onLogin);
    };
  }, []);
}
